import type { PPIMode } from "./coords.js";
import type { RadarTheme } from "./colorRamp.js";

/**
 * Per-client display state. This is the radar's "knobs and switches" — range
 * scale, gain, sea/rain clutter, mode, EBL/VRM positions. By project rule
 * (CLAUDE.md hard rule #3) display state is local and never travels through
 * the source.
 */
export interface DisplayState {
  rangeNm: number;
  mode: PPIMode;
  /** 0..1, target paint brightness. */
  gain: number;
  /** 0..1, sea-clutter (STC) suppression. */
  sea: number;
  /** 0..1, rain-clutter (FTC) suppression. */
  rain: number;
  /** 0..1, transmitter tune. Off-tune dims target paint. */
  tune: number;
  /** Colour theme for radar returns. */
  theme: RadarTheme;
  /**
   * Echo-trail length in sim seconds. 0 disables trails. Modern HD-colour
   * radars typically offer 30 / 60 / 180 / 360 second presets.
   */
  echoTrailSeconds: number;
  /** Whether to show the EBL line(s). */
  ebl: { id: 1 | 2; bearing: number; visible: boolean }[];
  /** Whether to show the VRM ring(s). */
  vrm: { id: 1 | 2; rangeNm: number; visible: boolean }[];
  /**
   * Lights / night mode toggle. Not a standard radar facility — this
   * is an instructional overlay. When on:
   *   - Lit marks pulse at their declared light character (Fl, Iso,
   *     Q, Mo) on both the radar PPI and the chart panel.
   *   - The PPI ambient dims a touch so the lights read clearly.
   * Off (default): standard radar behaviour, no light animation.
   */
  nightMode: boolean;
  /**
   * Chart panel orientation. Defaults to north-up (the chart-table
   * convention) but can switch to head-up to match the PPI for
   * easier cross-reference. Independent of the radar's mode so a
   * student can have radar in head-up but the chart in north-up.
   */
  chartOrientation: "north-up" | "head-up";
  /**
   * Chart paint mode. When `ais`, vessels render as AIS triangles
   * with COG vectors (chart-plotter style). When `silhouette`, they
   * render as filled hull outlines with their navigation lights
   * shown — simulates what a navigator would see by eye, especially
   * useful at night / in fog.
   */
  chartVesselPaint: "ais" | "silhouette";
  /**
   * Hide the radar PPI entirely so the student trains on the chart
   * alone. Useful for chart-fix and pilotage drills where the radar
   * would give away the answer. The chart panel must be visible
   * when this is on (the SessionRunner handles that).
   */
  radarVisible: boolean;
  /**
   * Show/hide the area's navigation marks (buoys, towers, lights,
   * racing-mark dataset) on both the PPI and the chart panel. Useful
   * for de-cluttering a busy area like the central Solent during a
   * race scenario where 100+ marks would otherwise dominate the
   * paint, and for blank-canvas exercises ("identify this target
   * without the marks for reference"). Default on.
   */
  showMarks: boolean;
}

export const DEFAULT_DISPLAY_STATE: DisplayState = {
  rangeNm: 6,
  mode: "head-up",
  gain: 0.7,
  sea: 0.4,
  rain: 0.0,
  tune: 1.0,
  theme: "hd",
  echoTrailSeconds: 60,
  ebl: [
    { id: 1, bearing: 0, visible: false },
    { id: 2, bearing: 90, visible: false },
  ],
  vrm: [
    { id: 1, rangeNm: 1, visible: false },
    { id: 2, rangeNm: 3, visible: false },
  ],
  nightMode: false,
  chartOrientation: "north-up",
  chartVesselPaint: "ais",
  radarVisible: true,
  showMarks: true,
};
